import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet, Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Compass, LayoutGrid, MessageSquare, ListTodo, CalendarClock, CreditCard, ReceiptText, Bell, LogOut, Menu, X, Eye } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, exitViewAs } from "../../lib/api";
import { allSlugs } from "../../pages/client/programmeStructure";

const AUDIT_SLUGS = ["audit-direction", "audit-cible", "audit-offres"];

const NAV = [
  { to: "/espace", label: "Mon tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/espace/parcours", label: "Le programme", icon: Compass },
  { to: "/espace/hub", label: "The elevate hub", icon: LayoutGrid },
  { to: "/espace/a-revoir", label: "À revoir ensemble", icon: MessageSquare },
  { to: "/espace/taches", label: "Mes tâches", icon: ListTodo },
  { to: "/espace/planning", label: "Planning de nos RDVs", icon: CalendarClock },
];

const NAV_PAYMENT = [
  { to: "/espace/paiement", label: "Plan de paiement", icon: CreditCard },
  { to: "/espace/facture", label: "Facture", icon: ReceiptText },
];

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [customProg, setCustomProg] = useState(null);
  const { pathname } = useLocation();
  useEffect(() => { api.get("/client/hub-pages").then(({ data }) => setCustomProg(!!data.programme_custom)).catch(() => setCustomProg(false)); }, []);
  const progSlug = (pathname.match(/^\/espace\/programme\/([^/]+)/) || [])[1];
  const badProg = progSlug && customProg !== null && (customProg ? !allSlugs(true).includes(progSlug) : AUDIT_SLUGS.includes(progSlug));

  useEffect(() => { api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {}); }, []);
  const unread = notifs.filter((n) => !n.read).length;
  const dismiss = async (nid) => { await api.delete(`/notifications/${nid}`); setNotifs((p) => p.filter((n) => n.id !== nid)); };
  const clearAll = async () => { await api.delete("/notifications"); setNotifs([]); };

  const doLogout = async () => {
    if (user?.preview) { exitViewAs(); window.close(); window.location.href = "/admin"; return; }
    await logout(); navigate("/login");
  };

  const SidebarInner = (
    <>
      <div className="relative flex h-[72px] items-center px-8">
        <button data-testid="client-logo-home" onClick={() => { setMobile(false); navigate("/espace"); }} className="logo-elevate text-4xl transition-opacity hover:opacity-80" style={{ color: "#FFFCF6" }}>elevate</button>
        <span className="absolute inset-x-8 bottom-0 border-t border-cream/25" />
      </div>
      <div className="mx-8 mb-2 mt-5 flex items-center gap-3 rounded-xl bg-white/5 px-4 py-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-sm font-semibold text-white">
          {(user?.name || "?")[0]}
        </span>
        <div>
          <p className="text-sm font-medium text-cream">{user?.name}</p>
          <p className="text-xs text-cream/50">Espace membre</p>
        </div>
      </div>
      <nav className="mt-3 flex-1 space-y-1.5 px-5">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setMobile(false)}
            data-testid={`client-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition ${
                isActive ? "bg-white/15 text-white" : "text-cream/70 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <item.icon size={18} />
            {item.label}
          </NavLink>
        ))}

        <div className="mx-1 mb-6 mt-4 border-t border-cream/25" />

        {NAV_PAYMENT.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setMobile(false)}
            data-testid={`client-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition ${
                isActive ? "bg-white/15 text-white" : "text-cream/70 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <item.icon size={18} />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-5">
        <button data-testid="client-logout-button" onClick={doLogout}
          className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm text-cream/70 transition hover:bg-white/10 hover:text-white">
          <LogOut size={18} /> Se déconnecter
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-cream">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-72 flex-col border-r border-white/10 bg-gradient-to-b from-bordeaux to-bordeaux-dark lg:flex">
        {SidebarInner}
      </aside>

      {mobile && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0" onClick={() => setMobile(false)} />
          <aside data-testid="client-mobile-menu" className="absolute inset-y-0 left-0 flex w-1/2 min-w-[250px] animate-[slideIn_.25s_ease-out] flex-col overflow-y-auto bg-gradient-to-b from-bordeaux to-bordeaux-dark shadow-[12px_0_40px_rgba(45,24,30,0.25)]" onClick={(e) => { if (e.target.closest("a")) setMobile(false); }}>
            <button data-testid="client-mobile-menu-close" onClick={() => setMobile(false)} className="absolute right-4 top-4 z-10 text-cream/70"><X size={20} /></button>
            {SidebarInner}
          </aside>
        </div>
      )}

      <div className="min-w-0 flex-1 lg:pl-72">
        {user?.preview && (
          <div data-testid="preview-banner" className="relative z-30 flex flex-wrap items-center justify-center gap-3 bg-[#2D181E] px-4 py-2 text-center text-xs text-white sm:text-sm">
            <Eye size={15} className="text-[#F3D2D9]" />
            <span>Aperçu de l'espace de <b>{user.first_name || user.name}</b> — lecture seule, rien n'est modifié</span>
            <button data-testid="preview-exit" onClick={doLogout} className="rounded-full bg-bordeaux px-3 py-1 text-xs font-medium text-white shadow-[0_0_14px_rgba(123,17,58,0.6)]">Fermer l'aperçu</button>
          </div>
        )}
        <header className="sticky top-0 z-20 flex h-[72px] items-center justify-between border-b border-line bg-cream/80 px-6 backdrop-blur-md lg:px-10">
          <button data-testid="client-mobile-menu-button" className="lg:hidden text-bordeaux" onClick={() => setMobile(true)}><Menu size={24} /></button>
          <div className="ml-auto flex items-center gap-4">
            <div className="relative">
              <button data-testid="client-notifications-button" onClick={() => setOpen((o) => !o)}
                className="relative rounded-full border border-line bg-white p-2.5 text-ink-soft transition hover:text-bordeaux">
                <Bell size={18} />
                {unread > 0 && <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-bordeaux text-[10px] text-white">{unread}</span>}
              </button>
              {open && (
                <div className="absolute right-0 mt-2 w-80 rounded-2xl border border-line bg-white p-3 shadow-card">
                  <div className="flex items-center justify-between px-2 py-1">
                    <p className="text-sm font-semibold text-ink">Notifications</p>
                    {notifs.length > 0 && <button data-testid="client-notif-clear-all" onClick={clearAll} className="text-xs text-bordeaux hover:underline">Tout effacer</button>}
                  </div>
                  {notifs.length === 0 && <p className="px-2 py-3 text-sm text-ink-muted">Rien de neuf.</p>}
                  {notifs.slice(0, 8).map((n) => (
                    <div key={n.id} className="group flex items-start gap-2 rounded-lg px-2 py-2 text-sm text-ink-soft hover:bg-bordeaux-tint">
                      <span className="flex-1">{n.message}</span>
                      <button data-testid={`client-notif-dismiss-${n.id}`} onClick={() => dismiss(n.id)} className="mt-0.5 shrink-0 text-ink-muted transition hover:text-bordeaux"><X size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-bordeaux text-sm font-semibold text-white">{(user?.name || "?")[0]}</span>
              <span className="hidden text-sm font-medium text-ink sm:block">{user?.name}</span>
            </div>
          </div>
        </header>
        <main className="px-6 py-8 lg:px-10 lg:py-10">
          {customProg === null ? null : badProg ? <Navigate to="/espace/parcours" replace /> : <Outlet context={{ customProg }} />}
        </main>
      </div>
      <a
        href="https://wa.me/33769310806"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-contact-button"
        aria-label="Contacter Fanny sur WhatsApp"
        className="group fixed bottom-6 right-6 z-40 flex items-center gap-3 rounded-full bg-white p-2 shadow-[0_12px_32px_rgba(45,24,30,0.22)] transition-all duration-300 hover:pr-5 hover:shadow-[0_16px_40px_rgba(123,17,58,0.35)]"
      >
        <span className="relative flex h-12 w-12 items-center justify-center rounded-full bg-bordeaux text-white transition-transform duration-300 group-hover:rotate-[-8deg] group-hover:scale-105">
          <span className="absolute inset-0 animate-ping rounded-full bg-bordeaux/30" style={{ animationDuration: "2.5s" }} />
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true" className="relative"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2Zm5.8 14.1c-.24.68-1.42 1.3-1.95 1.34-.5.05-1.12.07-1.8-.11-.42-.13-.95-.31-1.63-.6-2.87-1.24-4.74-4.13-4.88-4.32-.14-.19-1.17-1.55-1.17-2.96s.74-2.1 1-2.39c.27-.29.58-.36.77-.36h.56c.18 0 .42-.07.66.5.24.58.82 2 .89 2.15.07.14.12.31.02.5-.1.19-.14.31-.29.48-.14.17-.3.37-.43.5-.14.14-.29.3-.13.59.17.29.74 1.22 1.58 1.97 1.09.97 2 1.27 2.29 1.42.29.14.46.12.62-.07.17-.19.72-.84.91-1.13.19-.29.38-.24.65-.14.26.1 1.68.79 1.97.94.29.14.48.21.55.33.07.12.07.7-.17 1.38Z" /></svg>
        </span>
        <span className="max-w-0 overflow-hidden whitespace-nowrap text-sm font-medium text-bordeaux-dark transition-all duration-300 group-hover:max-w-[180px]">Écris-moi sur WhatsApp</span>
      </a>
    </div>
  );
}
